/**
 * Copy for /use-cases/legal-deposition-meeting-copilot.
 *
 * Compliance gate (ME-145 / MINA-49 / MINA-53): this page intentionally
 * avoids naming specific compliance frameworks (HIPAA, GDPR, SOC 2) or
 * making legal-grade claims about evidentiary admissibility or
 * attorney-client privilege. The factual posture is limited to what the
 * engineering team can verify today: audio is captured by the desktop app,
 * processed through a narrow cloud AI chain (Deepgram + OpenAI/Groq/Anthropic)
 * with zero data retention by those providers, the transcript and notes stay
 * in the lawyer's desktop app, and no third-party meeting bot joins the call.
 * Full subprocessor list lives at /trust.
 *
 * Privacy review packet 2026-05-20 is explicit: do NOT make compliance claims
 * that would require legal review.
 */
import type { Lang } from "../index";

export interface LegalDepositionMeetingCopilotCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: { heading: string; body: string; bullets: string[] };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: LegalDepositionMeetingCopilotCopy = {
  seo: {
    title:
      "Legal deposition meeting copilot — live English answer during the call",
    description:
      "For lawyers and paralegals on English depositions and discovery calls. Live answer drafts during the conversation, no bot in the call, transcripts stay in your desktop app. Cloud chain is documented end-to-end on our Trust page.",
  },
  eyebrow: "Use case · Legal",
  h1: "Live English answer copilot for deposition and discovery calls.",
  lede:
    "Lawyers and paralegals working English depositions and discovery calls need to respond precisely and quickly. Minuta listens, drafts answers grounded in your prep documents, and keeps the transcript in your desktop app — no bot in the call, no third-party recording the session.",
  problem: {
    heading: "Why third-party notetakers are a non-starter",
    body1:
      "On a privileged call — remote deposition, settlement discussion, or witness prep — the firm typically cannot have the audio uploaded and stored on a third-party SaaS. A bot joining the meeting also signals to the other side that an external tool is recording the conversation, which changes the dynamic.",
    body2:
      "The status quo is frantic note-taking and reconstructing the call from memory. Minuta captures audio on the lawyer's Mac, sends a narrow audio payload to transcription and summarization AI with documented zero-data-retention subprocessors, then stores the transcript in the desktop app. The full chain is published on the Trust page.",
  },
  scenarios: {
    heading: "Where it earns its keep",
    items: [
      {
        tag: "Remote deposition",
        title: "English deposition with a non-native team",
        body: "When the deposition is in English and part of the legal team is operating in a second language, live answer drafts help counsel respond precisely without losing the thread of the witness's testimony.",
      },
      {
        tag: "Discovery call",
        title: "Technical discovery in English",
        body: "On a fast-moving discovery call, the copilot surfaces answers grounded in the case documents you loaded before the call — so you can stay present instead of flipping through notes.",
      },
      {
        tag: "Multilingual depositions",
        title: "EN witness, PT or ES counsel",
        body: "When the witness speaks English and counsel operates in Portuguese or Spanish, Minuta's live translation gives the legal team a working English transcript in real time.",
      },
    ],
  },
  how: {
    heading: "How it fits a legal workflow",
    body: "Open Minuta a minute before the call. Attach the matter brief, deposition outline, or any prep documents. Run the call as you normally would — Zoom, Teams, or a phone bridge.",
    bullets: [
      "No bot joins. Other participants see no extra attendee. Audio is captured by the desktop app on the lawyer's Mac.",
      "A narrow audio payload goes to Deepgram for primary transcription, with OpenAI/Groq fallback. All documented subprocessors operate under zero data retention agreements — full list at /trust.",
      "Live copilot answer drafts appear on the side of your screen, grounded in the documents you loaded before the call.",
      "Transcript and notes stay in the desktop app on your machine. Nothing is stored on Minuta's servers beyond the immediate processing window.",
    ],
  },
  proof: {
    heading: "Why lawyers reach for Minuta on sensitive calls",
    bullets: [
      "No bot in the call — other participants see no extra attendee and no recording indicator from an external tool.",
      "Cloud chain is narrow and documented: Deepgram (transcription) + OpenAI/Groq/Anthropic (copilot and summary) — all with zero data retention. See /trust for the full subprocessor list.",
      "Working transcripts cover EN, PT, and ES — useful in matters that span jurisdictions.",
      "Official transcripts and certified translations remain a separate, lawyer-led workflow. Minuta is a working tool, not a replacement for court reporters.",
    ],
  },
  cta: {
    heading: "Try it on your next prep call",
    body: "7 days with everything unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: LegalDepositionMeetingCopilotCopy = {
  seo: {
    title:
      "Copiloto de reunião para depoimentos em inglês — resposta ao vivo durante a call",
    description:
      "Para advogados e paralegais em depoimentos e discovery calls em inglês. Rascunhos de respostas ao vivo durante a conversa, sem robô na reunião, transcrições ficam no app desktop. A cadeia cloud está documentada de ponta a ponta na página de Trust.",
  },
  eyebrow: "Caso de uso · Jurídico",
  h1: "Copiloto de resposta ao vivo para depoimentos e discovery calls em inglês.",
  lede:
    "Advogados e paralegais que atuam em depoimentos e discovery calls em inglês precisam responder com precisão e rapidez. O Minuta escuta, sugere respostas baseadas nos documentos de preparação e mantém a transcrição no app desktop — sem robô na call, sem terceiros gravando a sessão.",
  problem: {
    heading: "Por que os notetakers de terceiros são inviáveis",
    body1:
      "Em chamadas privilegiadas — depoimentos remotos, discussões de acordo ou preparação de testemunhas —, o escritório normalmente não pode ter o áudio enviado e armazenado em um SaaS de terceiros. Um robô entrando na reunião também sinaliza ao outro lado que uma ferramenta externa está gravando a conversa.",
    body2:
      "O padrão atual é a anotação frenética e a reconstrução da call de memória. O Minuta captura o áudio no Mac do advogado, envia um payload de áudio restrito para IA de transcrição e sumarização com subprocessadores com retenção zero documentada, e armazena a transcrição no app desktop. A cadeia completa está publicada na página de Trust.",
  },
  scenarios: {
    heading: "Onde se justifica o investimento",
    items: [
      {
        tag: "Depoimento remoto",
        title: "Depoimento em inglês com equipe não-nativa",
        body: "Quando o depoimento é em inglês e parte da equipe jurídica opera em um segundo idioma, os rascunhos de resposta ao vivo ajudam os advogados a responder com precisão sem perder o fio do depoimento.",
      },
      {
        tag: "Discovery call",
        title: "Discovery técnico em inglês",
        body: "Em uma discovery call acelerada, o copiloto sugere respostas baseadas nos documentos do caso carregados antes da call — para você se manter presente em vez de folhear anotações.",
      },
      {
        tag: "Depoimentos multilíngues",
        title: "Testemunha em EN, advogado em PT ou ES",
        body: "Quando a testemunha fala inglês e o advogado opera em português ou espanhol, a tradução ao vivo do Minuta entrega uma transcrição de trabalho em inglês em tempo real.",
      },
    ],
  },
  how: {
    heading: "Como entra no fluxo jurídico",
    body: "Abra o Minuta um minuto antes da chamada. Anexe resumos, roteiros ou qualquer documento de preparação. Execute a chamada normalmente — Zoom, Teams ou telefone.",
    bullets: [
      "Sem bots na chamada. Os outros participantes não veem novos usuários. O áudio é capturado pelo app desktop no Mac do advogado.",
      "Um payload restrito de áudio vai para o Deepgram para transcrição principal, com fallback OpenAI/Groq. Todos os subprocessadores documentados operam sob acordos de retenção zero — lista completa em /trust.",
      "Rascunhos de resposta do copiloto aparecem ao lado da tela, fundamentados nos documentos carregados antes da call.",
      "A transcrição e as anotações ficam no app desktop na sua máquina. Nada é armazenado nos servidores do Minuta além da janela de processamento imediata.",
    ],
  },
  proof: {
    heading: "Por que equipes jurídicas usam o Minuta em chamadas sensíveis",
    bullets: [
      "Sem robô na call — outros participantes não veem participante extra nem indicador de gravação de ferramenta externa.",
      "A cadeia cloud é restrita e documentada: Deepgram (transcrição) + OpenAI/Groq/Anthropic (copiloto e resumo) — todos com retenção zero. Veja /trust para a lista completa de subprocessadores.",
      "Transcrições de trabalho cobrem inglês, português e espanhol — útil em casos que cruzam jurisdições.",
      "Transcrições oficiais e traduções juramentadas continuam em fluxos separados conduzidos pelo advogado. O Minuta é ferramenta de trabalho, não substituto de estenógrafo.",
    ],
  },
  cta: {
    heading: "Teste na sua próxima chamada de preparação",
    body: "7 dias com tudo liberado. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar para macOS",
  },
};

const es: LegalDepositionMeetingCopilotCopy = {
  seo: {
    title:
      "Copiloto de reunión para deposiciones en inglés — respuesta en vivo durante la call",
    description:
      "Para abogados y paralegales en deposiciones y discovery calls en inglés. Borradores de respuesta en vivo durante la conversación, sin bot en la reunión, transcripciones se quedan en tu app de escritorio. La cadena cloud está documentada de extremo a extremo en nuestra página de Trust.",
  },
  eyebrow: "Caso de uso · Legal",
  h1: "Copiloto de respuesta en vivo para deposiciones y discovery calls en inglés.",
  lede:
    "Abogados y paralegales que trabajan en deposiciones y discovery calls en inglés necesitan responder con precisión y rapidez. Minuta escucha, sugiere respuestas basadas en tus documentos de preparación y mantiene la transcripción en tu app de escritorio — sin bot en la call, sin terceros grabando la sesión.",
  problem: {
    heading: "Por qué los notetakers de terceros no funcionan",
    body1:
      "En llamadas privilegiadas — deposiciones remotas, discusiones de acuerdo o preparación de testigos —, el despacho típicamente no puede tener el audio subido y almacenado en un SaaS de terceros. Un robot entrando a la reunión también señala al otro lado que una herramienta externa está grabando la conversación.",
    body2:
      "El estándar actual es la anotación frenética y la reconstrucción de la call de memoria. Minuta captura el audio en la Mac del abogado, envía un payload de audio restringido a IA de transcripción y resumen con subprocesadores con retención cero documentados, y almacena la transcripción en la app de escritorio. La cadena completa está publicada en la página de Trust.",
  },
  scenarios: {
    heading: "Dónde se justifica la inversión",
    items: [
      {
        tag: "Deposición remota",
        title: "Deposición en inglés con equipo no nativo",
        body: "Cuando la deposición es en inglés y parte del equipo legal opera en un segundo idioma, los borradores de respuesta en vivo ayudan a los abogados a responder con precisión sin perder el hilo del testimonio.",
      },
      {
        tag: "Discovery call",
        title: "Discovery técnico en inglés",
        body: "En una discovery call acelerada, el copiloto sugiere respuestas basadas en los documentos del caso cargados antes de la call — para que puedas mantenerte presente en vez de hojear notas.",
      },
      {
        tag: "Deposiciones multilingüe",
        title: "Testigo en EN, abogado en PT o ES",
        body: "Cuando el testigo habla inglés y el abogado opera en portugués o español, la traducción en vivo de Minuta entrega una transcripción de trabajo en inglés en tiempo real.",
      },
    ],
  },
  how: {
    heading: "Cómo entra en el flujo legal",
    body: "Abre Minuta un minuto antes de la llamada. Adjunta resúmenes, guiones o cualquier documento de preparación. Ejecuta la llamada normalmente — Zoom, Teams o teléfono.",
    bullets: [
      "Sin bots en la llamada. Los otros participantes no ven usuarios adicionales. El audio es capturado por la app de escritorio en la Mac del abogado.",
      "Un payload restringido de audio va a Deepgram para transcripción principal, con fallback OpenAI/Groq. Todos los subprocesadores documentados operan bajo acuerdos de retención cero — lista completa en /trust.",
      "Los borradores de respuesta del copiloto aparecen al lado de la pantalla, fundamentados en los documentos cargados antes de la call.",
      "La transcripción y las notas se quedan en la app de escritorio en tu máquina. Nada se almacena en los servidores de Minuta más allá de la ventana de procesamiento inmediato.",
    ],
  },
  proof: {
    heading: "Por qué los equipos legales usan Minuta en llamadas sensibles",
    bullets: [
      "Sin bot en la call — otros participantes no ven participante adicional ni indicador de grabación de herramienta externa.",
      "La cadena cloud es restringida y documentada: Deepgram (transcripción) + OpenAI/Groq/Anthropic (copiloto y resumen) — todos con retención cero. Ver /trust para la lista completa de subprocesadores.",
      "Las transcripciones de trabajo cubren inglés, portugués y español — útil en asuntos que cruzan jurisdicciones.",
      "Las transcripciones oficiales y traducciones juradas continúan en flujos separados dirigidos por el abogado. Minuta es una herramienta de trabajo, no un sustituto del taquígrafo.",
    ],
  },
  cta: {
    heading: "Pruébalo en tu próxima llamada de preparación",
    body: "7 días con todo desbloqueado. Sin tarjeta. €9/mes después.",
    primary: "Comenzar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const legalDepositionMeetingCopilotCopy: Record<
  Lang,
  LegalDepositionMeetingCopilotCopy
> = { en, pt, es };
